import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import Sidebar from '../components/Sidebar';
import toast from 'react-hot-toast';
import './Bots.css';

const API = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

/* ── Bot catalogue ────────────────────────────────────────────────────── */
const BOT_CATALOGUE = [
  {
    id: 'dca-btc',
    name: 'BTC Accumulation',
    tag: 'FREE',
    free: true,
    frequency: 'Weekly · DCA',
    risk: 'Low',
    riskLevel: 1,
    strategy: 'Dollar-Cost Averaging',
    asset: 'BTC',
    description: 'Automated Bitcoin weekly accumulation strategy using DCA. Buys at fixed intervals regardless of price, reducing timing risk.',
    winRate: 68,
    avgReturn: '+4.2%',
    color: '#f59e0b',
    icon: '₿',
  },
  {
    id: 'dca-eth',
    name: 'ETH DCA Pro',
    tag: 'FREE',
    free: true,
    frequency: 'Daily · DCA',
    risk: 'Low',
    riskLevel: 1,
    strategy: 'Dollar-Cost Averaging',
    asset: 'ETH',
    description: 'Advanced Ethereum DCA bot utilizing RSI indicators to time entries more precisely within a DCA framework.',
    winRate: 71,
    avgReturn: '+5.1%',
    color: '#a78bfa',
    icon: 'Ξ',
  },
  {
    id: 'rsi-momentum',
    name: 'RSI Momentum Pro',
    tag: 'CHALLENGE',
    free: false,
    frequency: 'Intraday · Momentum',
    risk: 'Medium',
    riskLevel: 2,
    strategy: 'trend_follow',
    asset: 'BTC',
    description: 'Combines RSI oversold/overbought signals with MACD crossovers to catch high-probability momentum entries.',
    winRate: 74,
    avgReturn: '+8.3%',
    color: '#06b6d4',
    icon: '⚡',
  },
  {
    id: 'breakout-hunter',
    name: 'Breakout Hunter',
    tag: 'CHALLENGE',
    free: false,
    frequency: 'Swing · Breakout',
    risk: 'Medium',
    riskLevel: 2,
    strategy: 'breakout',
    asset: 'ETH',
    description: 'Detects EMA breakouts on multiple timeframes, entering on confirmed volume spikes with tight stop-loss management.',
    winRate: 69,
    avgReturn: '+9.7%',
    color: '#10b981',
    icon: '🎯',
  },
  {
    id: 'mean-reversion',
    name: 'Mean Reversion Elite',
    tag: 'CHALLENGE',
    free: false,
    frequency: 'Intraday · Mean Rev',
    risk: 'Medium-High',
    riskLevel: 3,
    strategy: 'mean_reversion',
    asset: 'SOL',
    description: 'Exploits Bollinger Band squeezes and order flow imbalances to capture short-term mean reversion moves.',
    winRate: 66,
    avgReturn: '+11.2%',
    color: '#f472b6',
    icon: '◈',
  },
  {
    id: 'scalper-elite',
    name: 'Scalper X',
    tag: 'CHALLENGE',
    free: false,
    frequency: 'High Freq · Scalp',
    risk: 'High',
    riskLevel: 4,
    strategy: 'momentum',
    asset: 'BTC',
    description: 'High-frequency momentum scalper executing multiple trades per session. Maximizes volume for fast challenge completion.',
    winRate: 62,
    avgReturn: '+14.8%',
    color: '#ef4444',
    icon: '⚡',
  },
];

const RISK_COLORS = { 1: '#10b981', 2: '#f59e0b', 3: '#f97316', 4: '#ef4444' };
const RISK_LABELS = { 1: 'Low', 2: 'Medium', 3: 'Med-High', 4: 'High' };

/* ── Mini sparkline svg ─────────────────────────────────────────────────── */
function Sparkline({ color, up = true }) {
  const pts = up
    ? [0,60, 20,52, 40,55, 60,38, 80,30, 100,22, 120,18, 140,8]
    : [0,10, 20,18, 40,14, 60,30, 80,38, 100,45, 120,40, 140,55];
  const path = pts.reduce((acc, v, i) => acc + (i % 2 === 0 ? (i === 0 ? `M${v},` : ` L${v},`) : v), '');
  return (
    <svg width="140" height="60" viewBox="0 0 140 60" fill="none">
      <defs>
        <linearGradient id={`sg-${color.replace('#','')}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.35" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>
      <path d={path + ` L140,60 L0,60 Z`} fill={`url(#sg-${color.replace('#','')})`} />
      <path d={path} stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

/* ── Bot card ───────────────────────────────────────────────────────────── */
function BotCard({ bot, hasChallenge, activeChallengeId, onStart }) {
  const navigate = useNavigate();
  const locked = !bot.free && !hasChallenge;
  const canStart = bot.free || hasChallenge;

  return (
    <motion.div
      className={`bot-card ${locked ? 'bot-locked' : ''}`}
      initial={{ opacity: 0, y: 24 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
      style={{ '--bot-color': bot.color }}
    >
      {/* Tag */}
      <div className={`bot-tag ${bot.free ? 'bot-tag-free' : 'bot-tag-challenge'}`}>
        {bot.tag}
      </div>

      {/* Header */}
      <div className="bot-card-head">
        <div className="bot-icon" style={{ background: `${bot.color}1a`, color: bot.color }}>
          {bot.icon}
        </div>
        <div>
          <h3 className="bot-name">{bot.name}</h3>
          <p className="bot-freq">{bot.frequency}</p>
        </div>
      </div>

      {/* Risk bar */}
      <div className="bot-risk-row">
        <span className="bot-risk-label">Risk ——</span>
        <span className="bot-risk-value" style={{ color: RISK_COLORS[bot.riskLevel] }}>
          {RISK_LABELS[bot.riskLevel]}
        </span>
        <div className="bot-risk-bar">
          {[1,2,3,4].map(i => (
            <div key={i} className={`bot-risk-seg ${i <= bot.riskLevel ? 'filled' : ''}`}
              style={{ background: i <= bot.riskLevel ? RISK_COLORS[bot.riskLevel] : undefined }} />
          ))}
        </div>
      </div>

      {/* Description */}
      <p className="bot-desc">{bot.description}</p>

      {/* Sparkline + stats */}
      <div className="bot-chart-row">
        <div className="bot-sparkline">
          <Sparkline color={bot.color} up={bot.riskLevel <= 2} />
        </div>
        <div className="bot-stats">
          <div className="bot-stat">
            <span className="bot-stat-label">Win Rate</span>
            <span className="bot-stat-val" style={{ color: '#10b981' }}>{bot.winRate}%</span>
          </div>
          <div className="bot-stat">
            <span className="bot-stat-label">Avg Return</span>
            <span className="bot-stat-val" style={{ color: bot.color }}>{bot.avgReturn}</span>
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className="bot-actions">
        {canStart ? (
          <>
            <button
              className="btn-bot-primary"
              style={{ background: `linear-gradient(135deg, ${bot.color}, ${bot.color}cc)` }}
              onClick={() => onStart(bot, activeChallengeId)}
            >
              {bot.free ? `Start ${bot.asset} Bot` : 'Configure Bot'}
            </button>
            {!bot.free && (
              <button className="btn-bot-secondary" onClick={() => onStart(bot, activeChallengeId)}>
                View Details
              </button>
            )}
          </>
        ) : (
          <>
            <button className="btn-bot-primary btn-locked" onClick={() => navigate('/pricing')}>
              🔒 Buy Challenge
            </button>
            <button className="btn-bot-secondary" onClick={() => navigate('/pricing')}>
              View Plans
            </button>
          </>
        )}
      </div>
    </motion.div>
  );
}

/* ── Main page ──────────────────────────────────────────────────────────── */
export default function Bots() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [challenges, setChallenges] = useState([]);
  const [activeBot, setActiveBot] = useState(null);
  const [configBot, setConfigBot] = useState(null);
  const [prices, setPrices] = useState({});
  const [filter, setFilter] = useState('all'); // 'all' | 'free' | 'challenge'

  const token = localStorage.getItem('av_token');
  const activeChallenges = challenges.filter(c => ['active', 'phase2'].includes(c.status));
  const hasChallenge = activeChallenges.length > 0;
  const primaryChallenge = activeChallenges[0];

  useEffect(() => {
    if (!token) return;
    fetch(`${API}/challenges`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json())
      .then(d => setChallenges(d.challenges || []))
      .catch(() => {});

    fetch(`${API}/bot/prices`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json())
      .then(d => setPrices(d.prices || {}))
      .catch(() => {});
  }, [token]);

  const handleStartBot = (bot, challengeId) => {
    if (!challengeId && !bot.free) {
      navigate('/pricing');
      return;
    }
    setConfigBot(bot);
  };

  const handleRunBot = async () => {
    if (!primaryChallenge && !configBot?.free) {
      toast.error('No active challenge found');
      return;
    }
    const challengeId = primaryChallenge?._id;
    if (!challengeId) {
      toast.success(`${configBot.name} configured — free demo mode active`);
      setConfigBot(null);
      setActiveBot(configBot);
      return;
    }
    try {
      const r = await fetch(`${API}/bot/start/${challengeId}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error);
      toast.success(`${configBot.name} is live on your challenge!`);
      setConfigBot(null);
      setActiveBot(configBot);
      navigate(`/challenge/${challengeId}`);
    } catch (e) {
      toast.error(e.message);
    }
  };

  const filtered = filter === 'free'
    ? BOT_CATALOGUE.filter(b => b.free)
    : filter === 'challenge'
      ? BOT_CATALOGUE.filter(b => !b.free)
      : BOT_CATALOGUE;

  const totalBots = BOT_CATALOGUE.length;
  const activeBotCount = activeChallenges.filter(c => c.botRunning).length;

  return (
    <div className="app-layout">
      <Sidebar />
      <main className="main-content bots-page">

        {/* ── Hero banner ─────────────────────────────────────────────── */}
        <div className="bots-hero">
          <div className="bots-hero-glow" />
          <div className="bots-hero-glow bots-hero-glow-2" />

          <div className="bots-hero-content">
            <div className="bots-hero-left">
              <div className="bots-hero-badge">
                <span className="bots-hero-badge-dot" />
                AI-Powered Execution Engine
              </div>
              <h1 className="bots-hero-title">
                Trading Bots That<br />
                <span className="bots-hero-accent">Work While You Sleep</span>
              </h1>
              <p className="bots-hero-sub">
                Deploy institutional-grade strategies on your funded account.
                Let our bots handle execution — you collect the profits.
              </p>
              <div className="bots-hero-cta">
                {!hasChallenge && (
                  <Link to="/pricing" className="btn-hero-primary">
                    Get Funded to Unlock All Bots →
                  </Link>
                )}
                <a href="#catalogue" className="btn-hero-secondary">Browse Bots ↓</a>
              </div>
            </div>

            <div className="bots-hero-stats">
              <div className="bots-stat-card">
                <span className="bots-stat-icon">🤖</span>
                <div>
                  <div className="bots-stat-num">{totalBots}</div>
                  <div className="bots-stat-lbl">Total Bots</div>
                </div>
              </div>
              <div className="bots-stat-card">
                <span className="bots-stat-icon" style={{ color: '#10b981' }}>⚡</span>
                <div>
                  <div className="bots-stat-num" style={{ color: '#10b981' }}>{activeBotCount}</div>
                  <div className="bots-stat-lbl">Active Now</div>
                </div>
              </div>
              <div className="bots-stat-card">
                <span className="bots-stat-icon" style={{ color: '#a78bfa' }}>📈</span>
                <div>
                  <div className="bots-stat-num" style={{ color: '#a78bfa' }}>+6.3%</div>
                  <div className="bots-stat-lbl">Avg Monthly</div>
                </div>
              </div>
              <div className="bots-stat-card">
                <span className="bots-stat-icon" style={{ color: '#f59e0b' }}>🎯</span>
                <div>
                  <div className="bots-stat-num" style={{ color: '#f59e0b' }}>68%+</div>
                  <div className="bots-stat-lbl">Win Rate</div>
                </div>
              </div>
            </div>
          </div>

          {/* Live price ticker */}
          {Object.keys(prices).length > 0 && (
            <div className="bots-ticker">
              {Object.entries(prices).map(([asset, price]) => (
                <div key={asset} className="bots-ticker-item">
                  <span className="bots-ticker-asset">{asset}</span>
                  <span className="bots-ticker-price">${price.toLocaleString(undefined, { maximumFractionDigits: 2 })}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* ── Status bar (if has challenge) ──────────────────────────── */}
        {hasChallenge && (
          <div className="bots-status-bar">
            <div className="bots-status-indicator">
              <span className="bots-status-dot active" />
              Active Challenge: <strong>{primaryChallenge?.plan?.name || 'Challenge'}</strong>
            </div>
            <div className="bots-status-balance">
              Balance: <strong>${primaryChallenge?.currentBalance?.toLocaleString()}</strong>
            </div>
            <Link to={`/challenge/${primaryChallenge?._id}`} className="bots-status-link">
              View Challenge →
            </Link>
          </div>
        )}

        {/* ── Free bot notice (no challenge) ────────────────────────── */}
        {!hasChallenge && (
          <div className="bots-free-notice">
            <span>🎁</span>
            <div>
              <strong>2 Free Bots Available</strong> — No challenge required. Start with our free DCA bots today.
              <Link to="/pricing"> Upgrade to unlock all 6 bots →</Link>
            </div>
          </div>
        )}

        {/* ── Filter tabs ───────────────────────────────────────────── */}
        <div className="bots-filter-row" id="catalogue">
          <h2 className="bots-section-title">Bot Catalogue</h2>
          <div className="bots-filter-tabs">
            {['all', 'free', 'challenge'].map(f => (
              <button
                key={f}
                className={`bots-filter-tab ${filter === f ? 'active' : ''}`}
                onClick={() => setFilter(f)}
              >
                {f === 'all' ? 'All Bots' : f === 'free' ? '🎁 Free' : '🔒 Challenge'}
              </button>
            ))}
          </div>
        </div>

        {/* ── Bot grid ──────────────────────────────────────────────── */}
        <div className="bots-grid">
          <AnimatePresence mode="wait">
            {filtered.map((bot, i) => (
              <motion.div key={bot.id} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: i * 0.07 }}>
                <BotCard
                  bot={bot}
                  hasChallenge={hasChallenge}
                  activeChallengeId={primaryChallenge?._id}
                  onStart={handleStartBot}
                />
              </motion.div>
            ))}
          </AnimatePresence>
        </div>

        {/* ── How bots work ─────────────────────────────────────────── */}
        <div className="bots-how">
          <h2 className="bots-section-title" style={{ textAlign: 'center', marginBottom: 8 }}>How It Works</h2>
          <p className="bots-how-sub">Deploy in 3 steps</p>
          <div className="bots-how-steps">
            {[
              { icon: '◈', title: 'Choose a Bot', desc: 'Pick a strategy that fits your risk appetite — from low-risk DCA to high-frequency scalpers.' },
              { icon: '⚙', title: 'Connect Challenge', desc: 'Link your active funded challenge account. The bot uses your capital to trade automatically.' },
              { icon: '💰', title: 'Collect Profits', desc: 'Monitor performance on your dashboard and request your profit split payout anytime.' },
            ].map((s, i) => (
              <div key={i} className="bots-how-step">
                <div className="bots-how-num">{i + 1}</div>
                <div className="bots-how-icon">{s.icon}</div>
                <h3>{s.title}</h3>
                <p>{s.desc}</p>
              </div>
            ))}
          </div>
        </div>

      </main>

      {/* ── Configure modal ───────────────────────────────────────────── */}
      <AnimatePresence>
        {configBot && (
          <motion.div className="modal-overlay" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={() => setConfigBot(null)}>
            <motion.div className="bot-modal" initial={{ scale: 0.92, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.92, opacity: 0 }} onClick={e => e.stopPropagation()}>

              <div className="bot-modal-head">
                <div className="bot-icon large" style={{ background: `${configBot.color}1a`, color: configBot.color }}>
                  {configBot.icon}
                </div>
                <div>
                  <h2>{configBot.name}</h2>
                  <p className="bot-freq">{configBot.frequency}</p>
                </div>
                <button className="modal-close" onClick={() => setConfigBot(null)}>✕</button>
              </div>

              <div className="bot-modal-body">
                <div className="bot-modal-stat-row">
                  <div className="bot-modal-stat">
                    <span>Strategy</span><strong>{configBot.strategy?.replace('_', ' ')}</strong>
                  </div>
                  <div className="bot-modal-stat">
                    <span>Asset</span><strong>{configBot.asset}</strong>
                  </div>
                  <div className="bot-modal-stat">
                    <span>Win Rate</span><strong style={{ color: '#10b981' }}>{configBot.winRate}%</strong>
                  </div>
                  <div className="bot-modal-stat">
                    <span>Avg Return</span><strong style={{ color: configBot.color }}>{configBot.avgReturn}</strong>
                  </div>
                </div>

                <p className="bot-modal-desc">{configBot.description}</p>

                {hasChallenge ? (
                  <div className="bot-modal-challenge-info">
                    <div className="bots-status-dot active" style={{ display: 'inline-block', marginRight: 8 }} />
                    Will run on: <strong>{primaryChallenge?.plan?.name}</strong>
                    <span style={{ color: '#64748b', marginLeft: 8 }}>
                      (Balance: ${primaryChallenge?.currentBalance?.toLocaleString()})
                    </span>
                  </div>
                ) : configBot.free ? (
                  <div className="bot-modal-challenge-info" style={{ borderColor: 'rgba(16,185,129,0.25)', background: 'rgba(16,185,129,0.06)' }}>
                    🎁 Free bot — runs in demo mode with no challenge required.
                  </div>
                ) : (
                  <div className="bot-modal-challenge-info" style={{ borderColor: 'rgba(239,68,68,0.25)', background: 'rgba(239,68,68,0.06)' }}>
                    🔒 This bot requires an active challenge.{' '}
                    <Link to="/pricing" onClick={() => setConfigBot(null)}>Buy a challenge →</Link>
                  </div>
                )}
              </div>

              <div className="bot-modal-footer">
                <button className="btn-bot-secondary" onClick={() => setConfigBot(null)}>Cancel</button>
                <button
                  className="btn-bot-primary"
                  style={{ background: `linear-gradient(135deg, ${configBot.color}, ${configBot.color}bb)` }}
                  onClick={handleRunBot}
                  disabled={!configBot.free && !hasChallenge}
                >
                  {configBot.free ? `Start ${configBot.asset} Bot` : `Deploy on Challenge`}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
